// lib/leasing/sourceAgentMode.ts
//
// The Source composer's mode picker, as a data contract.
//
// It rides `ChatSession.collaborationMode`, the enum the session already carries, already defaults
// from the organisation, and already persists (prisma column, autosave through mutateInventory):
//
//   MANUAL = ASSISTED   ACCEPT EDITS = COLLABORATIVE   AUTO = AUTONOMOUS
//
// WHAT IT DOES TODAY, and read this before wiring anything to it: nothing enforceable. The three
// positions used to be three settings on an approval gate - a card the user had to click before a
// scoring edit, a launch or a mutation was written. That gate was removed wholesale on 2026-08-02
// at the owner's request, to be rebuilt differently, so the modes no longer decide whether ANY
// write happens. Every command applies when it runs, in all three positions.
//
// What is left is prompt-only: `sourceAgentModeLine` in lib/prompt/leasingKnowledgeBase.ts tells
// the agent how talkative the turn should be. That is a request to a model, not a permission
// boundary, and it must not be described as one. If you are rebuilding the gate, this file is the
// right anchor for it, but the enforcement has to be real code, not this string.
import type { ChatSession, CollaborationMode, SourceAgentMode } from '#types/types';
import { isEntityFocusSubject } from './session-focus';

export type { SourceAgentMode };

/** Picker order, and the 1/2/3 the popover shows next to each row. */
export const SOURCE_AGENT_MODES: readonly SourceAgentMode[] = ['MANUAL', 'ACCEPT_EDITS', 'AUTO'];

export const COLLABORATION_MODE_BY_SOURCE_AGENT_MODE: Record<SourceAgentMode, CollaborationMode> = {
  MANUAL: 'ASSISTED',
  ACCEPT_EDITS: 'COLLABORATIVE',
  AUTO: 'AUTONOMOUS',
};

/**
 * A session's mode read back off the enum it is stored in. Anything unrecognised resolves to
 * MANUAL: a mode we cannot read is a mode we have to ask under.
 */
function sourceAgentModeFromCollaborationMode(
  mode: CollaborationMode | null | undefined,
): SourceAgentMode {
  switch (mode) {
    case 'AUTONOMOUS':
      return 'AUTO';
    case 'COLLABORATIVE':
      return 'ACCEPT_EDITS';
    // Spelled out rather than left to `default`, so the exhaustiveness check can see every case
    // it is asked about. They all mean the same thing: a mode we cannot read is a mode we ask under.
    case 'ASSISTED':
    case null:
    case undefined:
    default:
      return 'MANUAL';
  }
}

export function normalizeSourceAgentMode(value: unknown): SourceAgentMode | null {
  return typeof value === 'string' && (SOURCE_AGENT_MODES as readonly string[]).includes(value)
    ? (value as SourceAgentMode)
    : null;
}

/**
 * Is this session one the Source mode picker governs? Same predicate the auto-recall gate in
 * kernel/useCommands.ts and the turn assembler in kernel/useAgents.ts use, deliberately: a mode
 * that applied to a slightly different set of sessions than the loop it rides would be a third
 * definition of "Source" to keep in sync.
 */
export function isSourceAgentSession(
  session: Pick<ChatSession, 'creationModule' | 'activeSubject'> | null | undefined,
): boolean {
  if (!session) return false;
  if (session.creationModule === 'source') return true;
  return (
    isEntityFocusSubject(session.activeSubject) &&
    session.activeSubject.focusActionType === 'ORCHESTRATE_SOURCING'
  );
}

/**
 * MAY SOURCE'S OWN UI STATE SHAPE THE SCREEN? Only while the active conversation IS a Source one.
 *
 * The open campaign, the focused or ticked shortlist rows and the new-campaign draft live in global
 * kernel state and deliberately survive navigation, so coming back to Source resumes where you
 * were. Portal read them on every surface, guarded by a DENY list (`!toolViewOwnsScreen`): every
 * other tool had to be named to be spared. The Studio dashboards were not, so after one visit to a
 * campaign, Studio Canvas and Studio List opened Source's chat column and an empty right bar
 * (owner, 2026-10-01: 'a grave bug, make sure it never happens again'). This is the ALLOW list
 * instead: a surface that is not a Source conversation never sees Source state, including the
 * next tool nobody remembers to add.
 */
export function sourceStateApplies(state: {
  activeChatSessionId: string | null;
  inventory: { ChatSession: { registry: Record<string, Pick<ChatSession, 'creationModule' | 'activeSubject'> | undefined> } };
}): boolean {
  const id = state.activeChatSessionId;
  return id !== null && isSourceAgentSession(state.inventory.ChatSession.registry[id]);
}

/**
 * The mode governing a session, or null when the picker does not apply to it (Studio, Slides,
 * Analyst, Master, global chat). Null means "leave the proposal gate exactly as it was".
 */
export function resolveSourceAgentMode(
  session: Pick<ChatSession, 'creationModule' | 'activeSubject' | 'collaborationMode'> | null | undefined,
): SourceAgentMode | null {
  if (!isSourceAgentSession(session)) return null;
  return sourceAgentModeFromCollaborationMode(session?.collaborationMode);
}
